import { Link } from "@tanstack/react-router";
import { X, ArrowUpRight } from "lucide-react";
import { Badge } from "@/components/badge";
import { FlyoverShell } from "./flyover-shell";
import {
  FormSnapshotBody,
  formSnapshotOriginLine,
  snapshotWeekLabel,
} from "./form-snapshot-detail";
import type { FormSnapshotData } from "../../../../shared/form-types";
import { AskAboutSourceAction } from "./ask-about-source-action";

const BTN_SMALL =
  "inline-flex min-h-[28px] items-center justify-center gap-[6px] rounded-lg border border-sand-200 bg-card px-[10px] text-xs font-semibold text-foreground hover:bg-sand-50";
function typeLabel(snapshot: FormSnapshotData): string {
  return snapshot.formType === "weekly_form" ? "Weekly" : "Monthly";
}

function eyebrow(snapshot: FormSnapshotData): string {
  return snapshot.formType === "weekly_form"
    ? "Source · Weekly form"
    : "Source · Monthly checkpoint";
}

/**
 * The functional-form snapshot flyover, per the comp #snapshot-flyover. Shares
 * the 472px FlyoverShell with the scorecard SourceFlyover. The orchestrator owns
 * selection state: pass the selected `snapshot` (null to close) and the
 * preceding weekly `prior` (for the derived change line), plus `onClose` and
 * `onEdit` (opens the in-card edit for this snapshot).
 *
 * "Ask about this" opens a focused chat launcher. The form remains pull-based:
 * its answers reach the model only if it calls readForm in that thread.
 */
export function FormSnapshotFlyover({
  rdSlug,
  snapshot,
  prior,
  onClose,
  onEdit,
}: {
  rdSlug: string;
  snapshot: FormSnapshotData | null;
  prior?: FormSnapshotData | null;
  onClose: () => void;
  onEdit: (formId: string) => void;
}) {
  return (
    <FlyoverShell
      openKey={snapshot?.id ?? null}
      onClose={onClose}
      ariaLabel="Form snapshot flyover"
    >
      {snapshot && (
        <>
          <div className="shrink-0 border-b border-sand-200 bg-card px-[22px] pb-[14px] pt-[18px]">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="mb-1.5 font-sans text-[10.5px] font-extrabold uppercase tracking-[0.08em] text-sand-400">
                  {eyebrow(snapshot)}
                </p>
                <h3 className="flex items-center gap-2 font-sans text-[17px] font-bold leading-[1.3] text-foreground">
                  {snapshotWeekLabel(snapshot)}
                  <Badge variant="category">{typeLabel(snapshot)}</Badge>
                </h3>
                <p className="mt-1.5 text-xs leading-[1.45] text-sand-400">
                  {formSnapshotOriginLine(snapshot, "Opened from Functional Forms")}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg text-sand-400 hover:bg-sand-100 hover:text-foreground"
              >
                <X className="h-[15px] w-[15px]" />
              </button>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <Link
                to="/rds/$rdId/forms/$formId"
                params={{ rdId: rdSlug, formId: snapshot.id }}
                className={BTN_SMALL}
              >
                Open full form
                <ArrowUpRight className="h-3 w-3" />
              </Link>
              <button
                type="button"
                onClick={() => onEdit(snapshot.id)}
                className={BTN_SMALL}
              >
                Edit
              </button>
              <AskAboutSourceAction rdSlug={rdSlug} source={{ type: "form", id: snapshot.id }} />
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto bg-sand-50 px-[22px] pb-[26px] pt-[18px]">
            <FormSnapshotBody snapshot={snapshot} prior={prior} />
          </div>
        </>
      )}
    </FlyoverShell>
  );
}
